import type { ClientPositionView, CreatePositionInput } from "@solomon-apps/shared";
import { WalletVerificationMethod } from "@solomon-apps/shared";

import type { RegisterWalletInput } from "@/services/clientWalletEnrollment";

import { sourceAddress, type VerifiedSource } from "./types";

export function buildRegisterWalletInput(args: {
    source: VerifiedSource;
    destination: string;
    manualReviewDescription?: string;
}): RegisterWalletInput {
    const input: RegisterWalletInput = {
        walletAddress: sourceAddress(args.source),
        rewardsDestinationAddress:
            args.destination.trim() ||
            (args.source.kind === "multisig-squads"
                ? (args.source.vaultAddress ?? args.source.multisigAddress)
                : args.source.address),
        ...(args.source.kind !== "manual" ? { sourceKind: args.source.kind } : {})
    };

    const proof = args.source.proof;
    if (proof) {
        input.verificationMethod =
            proof.method === "signMessage"
                ? WalletVerificationMethod.SignMessage
                : WalletVerificationMethod.MicroTransfer;
        input.verificationSignature = proof.signature;
        if (proof.verificationNonce) input.verificationNonce = proof.verificationNonce;
        if (proof.signedMessage) input.verificationSignedMessage = proof.signedMessage;
        if (proof.message) input.verificationMessage = proof.message;
    } else if (args.source.verificationMode === "direct-solomon") {
        input.verificationMode = "direct-solomon";
        input.manualReviewDescription = args.manualReviewDescription?.trim() || undefined;
    }

    if (args.source.kind === "multisig-squads") {
        if (args.source.signerAddress) {
            input.signerAddress = args.source.signerAddress;
        }
        if (args.source.multisigProgramId) {
            input.multisigProgramId = args.source.multisigProgramId;
        }
    }

    return input;
}

export function buildCreatePositionInput(
    args: Parameters<typeof buildRegisterWalletInput>[0] & { programSurfaceUuid: string }
): CreatePositionInput {
    const input = { ...buildRegisterWalletInput(args), programSurfaceUuid: args.programSurfaceUuid };
    if (!args.destination.trim()) delete input.rewardsDestinationAddress;
    return input;
}

/**
 * True when `positions` already holds an entry for this
 * (Program Surface, wallet address) pair — the identity the create call keys on
 * (see `sourceAddress`), so submitting again would be rejected as a conflict.
 * Lets the walkthrough flag the duplicate at Verify instead of after the user
 * signs and reaches Submit. Hidden positions are absent from the client list, so
 * the submit-time backend guard remains the backstop for those.
 */
export function hasExistingPositionForSource(
    positions: ClientPositionView[],
    programSurfaceUuid: string,
    walletAddress: string
): boolean {
    return positions.some(
        (position) => position.programSurface.uuid === programSurfaceUuid && position.account.address === walletAddress
    );
}
